import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Truck, ShieldCheck, PackageCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatINR } from "@/lib/api";
import { trackAddToCart, trackViewContent } from "@/lib/metaPixel";
import { useCart } from "@/contexts/CartContext";
import SizeSelector from "@/components/SizeSelector";
import PhotoUploader from "@/components/PhotoUploader";
import ProductCard from "@/components/ProductCard";
import QuantityStepper from "@/components/QuantityStepper";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const ProductDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [qty, setQty] = useState(() => Math.min(99, Math.max(1, parseInt(searchParams.get("qty") || "1", 10) || 1)));
    const { add } = useCart();
    const [product, setProduct] = useState(null);
    const [related, setRelated] = useState([]);
    const [size, setSize] = useState(null);
    const [photos, setPhotos] = useState({});
    const [galleryIdx, setGalleryIdx] = useState(0);
    const [selectedOptions, setSelectedOptions] = useState({});

    useEffect(() => {
        (async () => {
            const { data } = await api.get(`/products/${slug}`);
            setProduct(data.product);
            setRelated(data.related);
            setSize(data.product.sizes?.[0]);
            // Pre-select the first value for each dropdown ONLY when photos are required
            // (customization flow). For no-photo products (accessories/Funkos) the user
            // must actively pick an option — no pre-selection.
            const defaults = {};
            if (data.product.requires_photos !== false) {
                (data.product.options || []).forEach((o) => {
                    if (o.values && o.values.length) defaults[o.name] = o.values[0];
                });
            }
            setSelectedOptions(defaults);
            trackViewContent({
                id: data.product.id,
                name: data.product.name,
                value: data.product.base_price_inr || data.product.sizes?.[0]?.price_inr || 0,
            });
        })();
    }, [slug]);

    const requiresPhotos = product ? product.requires_photos !== false : true;
    const hasSizes = (product?.sizes || []).length > 0;

    const uploadedPaths = useMemo(() => Object.values(photos).map((p) => p.path).filter(Boolean), [photos]);

    const missingRequiredOption = () => {
        for (const o of product.options || []) {
            if (o.required && !selectedOptions[o.name]) return o.name;
        }
        return null;
    };

    const hasAnyOption = (product?.options || []).length > 0;
    const anyOptionSelected = Object.values(selectedOptions).some((v) => v);
    const noPhotoOptionMissing = !requiresPhotos && hasAnyOption && !anyOptionSelected;

    const addToCart = () => {
        const missing = missingRequiredOption();
        if (missing) {
            toast.error(`Please choose ${missing}`);
            return;
        }
        if (noPhotoOptionMissing) {
            toast.error("Please select an option before adding to cart");
            return;
        }
        if (requiresPhotos && uploadedPaths.length === 0) {
            toast.error("Please upload at least one reference photo before adding to cart");
            return;
        }
        add({
            product_id: product.id,
            product_name: product.name,
            size_label: size?.label || "Standard",
            unit_price_inr: size?.price_inr || product.base_price_inr,
            quantity: qty,
            selected_options: selectedOptions,
            reference_photos: uploadedPaths,
            cod_available: product.cod_available === true,
        });
        toast.success(qty > 1 ? `Added ${qty} to cart` : "Added to cart");
        trackAddToCart({
            id: product.id,
            name: product.name,
            value: (size?.price_inr || product.base_price_inr || 0) * qty,
        });
        navigate("/checkout");
    };

    if (!product) {
        return <div className="max-w-7xl mx-auto px-6 py-20 text-center"><Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" /></div>;
    }

    return (
        <div className="max-w-7xl mx-auto px-6 py-8" data-testid="product-detail-page">
            <div className="grid lg:grid-cols-2 gap-10">
                {/* Gallery */}
                <div>
                    <div className="rounded-4xl overflow-hidden aspect-square bg-gradient-to-br from-brand-cream via-white to-brand-lavender relative">
                        {product.images[galleryIdx] ? (
                            <img src={product.images[galleryIdx]} alt={product.name} className="w-full h-full object-cover" />
                        ) : null}
                    </div>
                    <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
                        {product.images.map((img, i) => (
                            <button key={i} onClick={() => setGalleryIdx(i)} className={`w-20 h-20 rounded-2xl overflow-hidden shrink-0 border-2 ${galleryIdx === i ? "border-primary" : "border-transparent"}`} data-testid={`thumb-${i}`}>
                                <img src={img} alt="" className="w-full h-full object-cover" />
                            </button>
                        ))}
                    </div>
                </div>

                {/* Info & customize */}
                <div>
                    <div className="text-xs uppercase tracking-widest text-brand-gold font-semibold">{product.category_slug}</div>
                    <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-1">{product.name}</h1>
                    <p className="text-muted-foreground mt-2">{product.tagline}</p>
                    <div className="mt-5 flex items-baseline gap-2">
                        <div className="font-display text-3xl font-bold text-primary" data-testid="product-price">{formatINR(size?.price_inr || product.base_price_inr)}</div>
                        <div className="text-xs text-muted-foreground">incl. all taxes</div>
                    </div>

                    <div className="mt-6 space-y-6">
                        {hasSizes && (
                            <div>
                                <h4 className="font-display text-sm font-semibold uppercase tracking-widest mb-3">Choose your size</h4>
                                <SizeSelector sizes={product.sizes} value={size} onChange={setSize} />
                            </div>
                        )}

                        {(product.options || []).length > 0 && (
                            <div className="space-y-3" data-testid="product-options">
                                {product.options.map((opt) => (
                                    <div key={opt.name}>
                                        <h4 className="font-display text-sm font-semibold uppercase tracking-widest mb-2">
                                            {opt.name}{opt.required && <span className="text-primary ml-1">*</span>}
                                        </h4>
                                        <select
                                            value={selectedOptions[opt.name] || ""}
                                            onChange={(e) => setSelectedOptions({ ...selectedOptions, [opt.name]: e.target.value })}
                                            data-testid={`product-option-${opt.name.toLowerCase().replace(/\s+/g, "-")}`}
                                            className="w-full h-11 rounded-2xl border bg-white/60 px-3 text-sm"
                                        >
                                            {!opt.required && <option value="">Choose {opt.name.toLowerCase()}…</option>}
                                            {(opt.values || []).map((v) => (
                                                <option key={v} value={v}>{v}</option>
                                            ))}
                                        </select>
                                    </div>
                                ))}
                            </div>
                        )}

                        {requiresPhotos && (
                            <div>
                                <h4 className="font-display text-sm font-semibold uppercase tracking-widest mb-3">Upload reference photos <span className="text-primary normal-case font-semibold">(required)</span></h4>
                                <PhotoUploader value={photos} onChange={setPhotos} />
                                <p className="text-[11px] text-muted-foreground mt-2">Please upload at least one photo of your subject (front, side and any special-detail shots help our artists match your subject perfectly).</p>
                            </div>
                        )}

                        <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Qty</span>
                                <QuantityStepper value={qty} onChange={setQty} size="lg" testId="product-qty" />
                            </div>
                            <Button
                                onClick={addToCart}
                                disabled={(hasSizes && !size) || noPhotoOptionMissing || (requiresPhotos && uploadedPaths.length === 0)}
                                data-testid="add-to-cart-btn"
                                className="rounded-full h-12 px-8 bg-primary hover:bg-primary/90 text-primary-foreground text-base"
                            >
                                <PackageCheck className="w-4 h-4" /> Add to Cart · {formatINR((size?.price_inr || product.base_price_inr) * qty)}
                            </Button>
                        </div>

                        {requiresPhotos && (
                            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-900 space-y-2" data-testid="no-return-notice">
                                <div><b>Please note:</b> As each miniature is custom-made from your photos, we do <b>not</b> accept returns or refunds once the order has been shipped. Review your design carefully before approval.</div>
                                <div><b>Please note:</b> The final model may not be an exact 100% replica of the preview or the reference photo. Minor variations in facial features, colors, proportions, and details may occur during the creation process, as each piece is handcrafted.</div>
                            </div>
                        )}

                        <div className="grid grid-cols-3 gap-2 pt-2">
                            <div className="text-center rounded-2xl glass p-3">
                                <Truck className="w-4 h-4 mx-auto text-primary" />
                                <div className="text-[11px] font-semibold mt-1">Delivery in 4–5 days</div>
                            </div>
                            <div className="text-center rounded-2xl glass p-3">
                                <ShieldCheck className="w-4 h-4 mx-auto text-primary" />
                                <div className="text-[11px] font-semibold mt-1">Secure payments</div>
                            </div>
                            <div className="text-center rounded-2xl glass p-3">
                                <PackageCheck className="w-4 h-4 mx-auto text-primary" />
                                <div className="text-[11px] font-semibold mt-1">Premium packaging</div>
                            </div>
                        </div>

                        {/* Details */}
                        <Accordion type="multiple" defaultValue={["desc"]} className="rounded-3xl glass px-5" data-testid="product-details-accordion">
                            <AccordionItem value="desc">
                                <AccordionTrigger data-testid="product-description-trigger">Description</AccordionTrigger>
                                <AccordionContent data-testid="product-description-content" className="whitespace-pre-line text-muted-foreground leading-relaxed">{product.description}</AccordionContent>
                            </AccordionItem>
                            <AccordionItem value="features">
                                <AccordionTrigger>Features</AccordionTrigger>
                                <AccordionContent>
                                    <ul className="list-disc pl-5 space-y-1">{product.features.map((f, i) => <li key={i}>{f}</li>)}</ul>
                                </AccordionContent>
                            </AccordionItem>
                            <AccordionItem value="material">
                                <AccordionTrigger>Material & Delivery</AccordionTrigger>
                                <AccordionContent>
                                    <p>Material: {product.material}</p>
                                    <p>Delivery: 4–5 business days (across India)</p>
                                    <p>Weight: {size?.weight_grams} grams</p>
                                </AccordionContent>
                            </AccordionItem>
                            {product.faqs.length > 0 && (
                                <AccordionItem value="faqs">
                                    <AccordionTrigger>FAQs</AccordionTrigger>
                                    <AccordionContent>
                                        <div className="space-y-3">{product.faqs.map((f, i) => (<div key={i}><div className="font-semibold text-sm">{f.q}</div><div className="text-sm text-muted-foreground">{f.a}</div></div>))}</div>
                                    </AccordionContent>
                                </AccordionItem>
                            )}
                        </Accordion>
                    </div>
                </div>
            </div>

            {related.length > 0 && (
                <div className="mt-20">
                    <h2 className="font-display text-3xl font-semibold mb-6">You may also love</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>
                </div>
            )}
        </div>
    );
};

export default ProductDetail;
